import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon, type IconName } from './Icon'
import css from './ActionMenu.module.css'

/**
 * Menu aksi.
 *
 * Satu komponen, dua penyaji: menu melayang di desktop, lembar geser dari
 * bawah di ponsel. Antarmuka lama membangun action sheet ponsel secara
 * lengkap tetapi fungsi pembukanya tidak pernah dipanggil sama sekali, dan
 * klik kanan hanya bekerja di tampilan kisi sementara tampilan bawaannya
 * adalah daftar — sehingga sebagian besar pengguna tidak pernah tahu menu
 * itu ada.
 */

export interface AksiMenu {
  id: string
  label: string
  icon: IconName
  onSelect: () => void
  /** Ditampilkan dengan warna bahaya dan dipisahkan dari aksi biasa. */
  danger?: boolean
  disabled?: boolean
  /** Keterangan kecil di bawah label, misalnya alasan aksi dinonaktifkan. */
  hint?: string
}

interface Props {
  open: boolean
  onClose: () => void
  aksi: AksiMenu[]
  /** Judul yang tampil di lembar geser ponsel — biasanya nama berkas. */
  judul?: string
  /** Titik jangkar untuk menu desktop. Bila kosong, dipusatkan. */
  anchor?: { x: number; y: number } | null
}

const LEBAR_MENU = 232

export function ActionMenu({ open, onClose, aksi, judul, anchor }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [posisi, setPosisi] = useState<{ top: number; left: number } | null>(null)
  const ponsel = usePonsel()

  useLayoutEffect(() => {
    if (!open || ponsel || !anchor) {
      setPosisi(null)
      return
    }

    const tinggi = ref.current?.offsetHeight ?? aksi.length * 40 + 16
    const margin = 8

    // Menu dibalik ke atas atau ke kiri bila tidak muat, supaya tidak pernah
    // terpotong di tepi layar.
    let top = anchor.y
    let left = anchor.x
    if (top + tinggi + margin > window.innerHeight) top = Math.max(margin, anchor.y - tinggi)
    if (left + LEBAR_MENU + margin > window.innerWidth) {
      left = Math.max(margin, window.innerWidth - LEBAR_MENU - margin)
    }

    setPosisi({ top, left })
  }, [open, anchor, ponsel, aksi.length])

  useEffect(() => {
    if (!open) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onScroll = () => onClose()

    document.addEventListener('keydown', onKey)
    // Menu melayang tidak ikut bergerak saat halaman digulir, jadi lebih baik
    // ditutup daripada menggantung di tempat yang salah.
    if (!ponsel) window.addEventListener('scroll', onScroll, true)

    return () => {
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open, onClose, ponsel])

  if (!open) return null

  const daftar = (
    <div className={css.list} role="menu">
      {aksi.map((a, i) => {
        const pemisah = a.danger && i > 0 && !aksi[i - 1]?.danger
        return (
          <div key={a.id}>
            {pemisah && <div className={css.divider} />}
            <button
              type="button"
              role="menuitem"
              className={`${css.item} ${a.danger ? css.danger : ''}`}
              disabled={a.disabled}
              onClick={() => {
                if (a.disabled) return
                onClose()
                a.onSelect()
              }}
            >
              <Icon name={a.icon} size={17} />
              <span className={css.itemLabel}>
                {a.label}
                {a.hint && <span className={css.itemHint}>{a.hint}</span>}
              </span>
            </button>
          </div>
        )
      })}
    </div>
  )

  if (ponsel) {
    return createPortal(
      <div className={css.sheetBackdrop} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className={css.sheet} role="dialog" aria-modal="true" aria-label={judul ?? 'Menu aksi'}>
          {judul && <div className={`${css.sheetTitle} truncate`}>{judul}</div>}
          {daftar}
        </div>
      </div>,
      document.body,
    )
  }

  return createPortal(
    <>
      <div className={css.backdrop} onMouseDown={onClose} onContextMenu={(e) => { e.preventDefault(); onClose() }} />
      <div
        ref={ref}
        className={css.menu}
        style={posisi ? { top: posisi.top, left: posisi.left } : { visibility: 'hidden' }}
      >
        {daftar}
      </div>
    </>,
    document.body,
  )
}

/** True pada perangkat sentuh atau layar sempit. */
export function usePonsel(): boolean {
  const [ponsel, setPonsel] = useState(
    () => window.matchMedia('(max-width: 640px), (pointer: coarse)').matches,
  )

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px), (pointer: coarse)')
    const ubah = (e: MediaQueryListEvent) => setPonsel(e.matches)
    mq.addEventListener('change', ubah)
    return () => mq.removeEventListener('change', ubah)
  }, [])

  return ponsel
}

/**
 * Tekan-lama untuk membuka menu di perangkat sentuh.
 *
 * 500 ms adalah ambang yang dipakai sistem operasi seluler; lebih pendek
 * membuat menu muncul saat pengguna sebenarnya hendak menggulir.
 * Gerakan lebih dari 10 px membatalkan tekanan, karena itu berarti menggulir.
 */
export function useTekanLama(onTekanLama: (titik: { x: number; y: number }) => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const awal = useRef<{ x: number; y: number } | null>(null)

  const batal = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    awal.current = null
  }

  return {
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0]
      if (!t) return
      awal.current = { x: t.clientX, y: t.clientY }
      timer.current = setTimeout(() => {
        // Getaran singkat menegaskan bahwa tekanan sudah terdaftar.
        navigator.vibrate?.(12)
        onTekanLama({ x: t.clientX, y: t.clientY })
        timer.current = null
      }, 500)
    },
    onTouchMove: (e: React.TouchEvent) => {
      const t = e.touches[0]
      const a = awal.current
      if (!t || !a) return
      if (Math.abs(t.clientX - a.x) > 10 || Math.abs(t.clientY - a.y) > 10) batal()
    },
    onTouchEnd: batal,
    onTouchCancel: batal,
  }
}
